import React, {createContext, useContext, useEffect, useState, useRef} from 'react';

import {parse} from '../utils/jwt';
import {useOktaAuth} from '@okta/okta-react';
import {config} from '../components/Session/authConfig';
import {showTimeoutDebugging} from '../utils/debug';
import {useLogger} from './Logger';

import { revokeToken } from '../utils/jwt';

import InactivityTimeout from '../components/InactivityTimeout/InactivityTimeout';
import { useUnload } from '../utils/hooks';
import {useFeatureFlags} from "../components/FeatureFlags/withFeatureFlags";
import {maskEmail} from '../utils/helpers';

/** @import { CurrentUserContextType, CurrentUserProviderComponent } from "./CurrentUserProviderTypes" */

export const CurrentUserContext = createContext(
    /** @type {CurrentUserContextType | null} */ (null)
);

export const withCurrentUser = (/** @type {React.FC<{ authUser: unknown; }>} */ Component) => {
    return (/** @type {object} */ props) => {
        return (
            <CurrentUserContext.Consumer>
                {state => <Component authUser={state} {...props} />}
            </CurrentUserContext.Consumer>
        )
    };
};

export const useCurrentUser = () => {
    return useContext(CurrentUserContext);
};

/** @type {CurrentUserProviderComponent} */
export default (props) => {

    const {oktaAuth, authState} = useOktaAuth();
    const [userContext, setUserContext] = useState(/** @type {CurrentUserContextType} */ ({}));
    const lastAuthStateRef = useRef(/** @type {{ isPending: boolean; isAuthenticated: boolean; customerId: string; accessToken: string; } | null} */ (null));    const logger = useLogger();
    
    useUnload(async () => {
        // @ts-expect-error I think this code is incorrect - the code seems to be talking about the AuthState object, but it is the OktaAuth object that is in use
        if (oktaAuth && oktaAuth.accessToken && oktaAuth.accessToken.accessToken) {
            // @ts-expect-error I think this code is incorrect - the code seems to be talking about the AuthState object, but it is the OktaAuth object that is in use
            await revokeToken(oktaAuth.accessToken.accessToken, true);
        }
    });

    const upsertUserContext = (
        /** @type {boolean} */ isPending,
        /** @type {string} */ accessToken,
        /** @type {boolean} */ isAuthenticated,
        /** @type {string} */ customerId
    ) => {
        const userContext = {
            isPending: isPending,
            accessToken: accessToken,
            isAuthenticated: isAuthenticated,
            customerId: customerId,
            logout: logout,
            login: login,
        };
        setUserContext(userContext);
    }

    const getCustomerId = (/** @type {string} */ token) => {
        if (!token) return '';
        return parse(token).investecId;
    };

    const login = async (/** @type {string} */ username, /** @type {string} */ password) => {
        const maskedEmail = maskEmail(username);
        logger.info(`Invoking Okta sign in for ${maskedEmail}`);
        try {
            const result = await oktaAuth.signInWithCredentials({
                username: username,
                password: password
            });

            // @ts-expect-error I am not sure if this is correct code - it doesn't match the Okta documentation
            if (result && result.data && result.data.status === "LOCKED_OUT") {
                logger.warn(`Okta locked out ${maskedEmail}`);
                const errorMessage = "You've reached the maximum number of attempts to log in. To unlock your account, reset your password by clicking \'having trouble logging in?\' below";
                throw new LockedOutError(errorMessage);
            }

            // TODO(sso-migration): Remove this fire-and-forget call (and the matching
            // /api/sso/map-user controller) once all users have been migrated to Okta
            // and password mirroring into the legacy SSO IdP is no longer required.
            // Map user to SSO provider (fire-and-forget, don't block login)
            try {
                logger.info(`Starting SSO password mapping for ${maskedEmail}`);
                await fetch('/api/sso/map-user', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: username,
                        password: password
                    })
                }).then(async response => {
                    // The endpoint returns a uniform `{ success: bool }` body and never
                    // echoes user details, so it is safe to log.
                    const body = await response.text().catch(() => '');
                    logger.info(
                        `SSO mapping request completed for ${maskedEmail} with status ${response.status} and response ${body}`,
                        { additionalData: { status: response.status, ok: response.ok, body: body } });
                }).catch(err => logger.warn(`SSO mapping request failed for ${maskedEmail}`, {}, err));
            } catch (ssoErr) {
                logger.warn(`SSO mapping failed for ${maskedEmail}`, {}, ssoErr);
            }

            logger.info(`Okta authentication succeeded for ${maskedEmail}, redirecting`);
            await oktaAuth.signInWithRedirect({
                sessionToken: result.sessionToken
            });
        } catch (e) {
            if (e instanceof LockedOutError) {
                logger.warn(`Okta locked out ${maskedEmail}`);
                throw e;
            } else {
                logger.warn(`Okta authentication failed for ${maskedEmail}`, {}, e);
                const errorMessage = "Your email or password is incorrect. Please double-check your details or click \'having trouble logging in?\' for assistance";
                throw new InvalidLoginError(errorMessage);
            }
        }
    };

    const logout = async (/** @type {string} */ redirectUri = "") => {
        logger.info(`Invoking Okta sign out for customer ${userContext.customerId || 'unknown'}`);
        // @ts-expect-error I am not sure if this is correct code - it doesn't match the Okta documentation
        await revokeToken(authState.accessToken.accessToken);
        // @ts-expect-error I am not sure if this is correct code - it doesn't match the Okta documentation
        await oktaAuth.token.revoke(authState.accessToken);
        await oktaAuth.signOut({
            postLogoutRedirectUri: redirectUri || config.logoutUri
        });
    }

    const handleLogout = async () => {
        logger.info(`Logout clicked for customer ${userContext.customerId || 'unknown'}`);
        await logout(config.logoutUri);
    }

    const handleOnInactive = async () => {
        logger.info(`Inactive, automatically logging out`);
        await logout(config.timeoutUri);
    };    
    useEffect(() => {
        // Get current values
        const isPending = authState === null;
        const accessToken = authState && authState.accessToken && authState.accessToken.accessToken || '';
        const isAuthenticated = Boolean(authState && authState.isAuthenticated);
        const customerId = (authState && authState.accessToken && getCustomerId(authState.accessToken.accessToken)) || '';

        // Check if this is just a token renewal (same user, same auth state, just new token)
        const lastState = lastAuthStateRef.current;
        const isTokenRenewal = lastState &&
            lastState.isAuthenticated === isAuthenticated &&
            lastState.customerId === customerId &&
            lastState.isPending === isPending &&
            isAuthenticated && // Only for authenticated users
            accessToken !== '' && // New token exists
            lastState.accessToken !== accessToken; // Token actually changed

        if (isTokenRenewal) {
            // Update our ref but don't trigger state change
            lastAuthStateRef.current = { isPending, isAuthenticated, customerId, accessToken };
            return;
        }

        // Only update if something meaningful has changed
        const hasChanged =
            !lastState ||
            lastState.isPending !== isPending ||
            lastState.isAuthenticated !== isAuthenticated ||
            lastState.customerId !== customerId ||
            (!isAuthenticated && lastState.accessToken !== accessToken);

        if (hasChanged) {
            upsertUserContext(isPending, accessToken, isAuthenticated, customerId);
            lastAuthStateRef.current = { isPending, isAuthenticated, customerId, accessToken };
        }
    }, [authState]);

    return (
        <CurrentUserContext.Provider value={userContext}>
            <InactivityTimeout
                {...props}
                user={userContext}
                onInactive={handleOnInactive}
                onLogout={handleLogout}
                debug={showTimeoutDebugging}>
                {props.children}
            </InactivityTimeout>
        </CurrentUserContext.Provider>
    );
};

class LockedOutError extends Error {
    constructor(/** @type {string} */ message) {
        super(message);

        this.name = "LockedOutError";
        this.message = message;
    }
}

class InvalidLoginError extends Error {
    constructor(/** @type {string} */ message) {
        super(message);

        this.name = "InvalidLoginError";
        this.message = message;
    }
}
